import type { ClassicEditor, EditorConfig } from 'ckeditor5'
import {
  Alignment,
  Autoformat,
  AutoLink,
  BlockQuote,
  Bold,
  Clipboard,
  CodeBlock,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  HorizontalLine,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SpecialCharacters,
  SpecialCharactersEssentials,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  Underline,
} from 'ckeditor5'
import { MultiLevelList } from 'ckeditor5-premium-features'

import { CK_EDITOR_LICENSE_KEY } from '../license_key'
import { CutPlugin } from '../plugins/CutPlugin'
import { IndentPaddingToMarginPlugin } from '../plugins/IndentPaddingToMarginPlugin'
import { NormalizeTableWidthPlugin } from '../plugins/NormalizeTableWidthPlugin'
import { PasteAsTextPlugin } from '../plugins/PasteAsTextPlugin'
import { PastePlugin } from '../plugins/PastePlugin'
import { ProcoreImageUploadPlugin } from '../plugins/ProcoreImageUploadPlugin'
import { TabSpacesPlugin } from '../plugins/TabSpacesPlugin'
import type { TextEditorImageUploadHandlers } from '../TextEditor.types'
import { getEditorTranslation, getValidEditorLocale } from './locale'

interface TextEditorConfigOptions {
  imageUpload?: TextEditorImageUploadHandlers
}

type ProcoreEditorConfig = EditorConfig & {
  procoreImageUpload?: TextEditorImageUploadHandlers
}

const TEXT_COLOR_PALETTE = [
  { color: '#BFEDD2' },
  { color: '#FBEEB8' },
  { color: '#F8CAC6' },
  { color: '#ECCAFA' },
  { color: '#C2E0F4' },
  { color: '#2DC26B' },
  { color: '#F1C40F' },
  { color: '#E03E2D' },
  { color: '#B96AD9' },
  { color: '#3598DB' },
  { color: '#169179' },
  { color: '#E67E23' },
  { color: '#BA372A' },
  { color: '#843FA1' },
  { color: '#236FA1' },
  { color: '#ECF0F1' },
  { color: '#CED4D9' },
  { color: '#95A5A6' },
  { color: '#7E8C8D' },
  { color: '#34495E' },
  { color: '#000000' },
  { color: '#FFFFFF' },
]

export const getDefaultConfig = (
  locale: string = 'en',
  options: TextEditorConfigOptions = {}
): EditorConfig => {
  const toolbar = [
    'bold',
    'italic',
    'underline',
    'strikethrough',
    '|',
    'alignment:left',
    'alignment:center',
    'alignment:right',
    '|',
    'bulletedList',
    'numberedList',
    'multiLevelList',
    '|',
    'outdent',
    'indent',
    '|',
    'cut',
    'paste',
    'pasteAsText',
    '|',
    'fontSize',
    '|',
    'fontColor',
    'fontBackgroundColor',
    '|',
    'insertTable',
    'link',
    '|',
    'blockquote',
    'heading',
    '|',
    'horizontalLine',
    'removeFormat',
    'specialCharacters',
    'subscript',
    'superscript',
    'codeblock',
    '|',
    'undo',
    'redo',
  ]

  const config: ProcoreEditorConfig = {
    licenseKey: CK_EDITOR_LICENSE_KEY,
    language: getValidEditorLocale(locale),
    translations: [getEditorTranslation(locale)],
    plugins: [
      Alignment,
      AutoLink,
      Bold,
      Clipboard,
      Essentials,
      FontBackgroundColor,
      FontColor,
      FontSize,
      GeneralHtmlSupport,
      Indent,
      IndentBlock,
      Italic,
      Link,
      List,
      ListProperties,
      MultiLevelList,
      Paragraph,
      PasteFromOffice,
      Strikethrough,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      Underline,
      CutPlugin,
      PastePlugin,
      PasteAsTextPlugin,
      IndentPaddingToMarginPlugin,
      NormalizeTableWidthPlugin,
      BlockQuote,
      Heading,
      Autoformat,
      SpecialCharacters,
      SpecialCharactersEssentials,
      Subscript,
      Superscript,
      HorizontalLine,
      RemoveFormat,
      CodeBlock,
      TabSpacesPlugin, // TODO - delete
    ],
    toolbar,
    fontSize: {
      options: [
        { title: '8pt', model: '8px' },
        { title: '10pt', model: '10px' },
        { title: '12pt', model: '12px' },
        { title: '14pt', model: '14px' },
        { title: '18pt', model: '18px' },
        { title: '24pt', model: '24px' },
        { title: '36pt', model: '36px' },
      ],
      supportAllValues: true,
    },
    fontColor: {
      colors: TEXT_COLOR_PALETTE,
    },
    fontBackgroundColor: {
      colors: TEXT_COLOR_PALETTE,
    },
    indentBlock: {
      offset: 40,
      unit: 'px',
    },
    htmlSupport: {
      allow: [
        {
          name: 'p',
          styles: {
            'margin-left': true,
          },
        },
      ],
    },
    link: {
      // Add a protocol to links typed without one (e.g. "www.google.com"),
      // so they are not treated as relative URLs.
      defaultProtocol: 'https://',
      decorators: {
        // Manual decorator renders an "Open in a new tab" switch in the link
        // form, letting the user choose new tab vs. same window per link.
        openInNewTab: {
          mode: 'manual',
          // The label doubles as CKEditor's own translation key (resolved by
          // editor.locale.t, not Procore I18n). Every locale this editor
          // supports (en, fr, es, de, ja, pl, pt, th, zh) already ships a
          // CKEditor translation for "Open in a new tab" (loaded in locale.ts),
          // so it is localized automatically and no Procore i18n entry is
          // needed. Do not replace with I18n.t(...): that would bypass
          // CKEditor's translator and duplicate existing translations.
          label: 'Open in a new tab',
          defaultValue: false, // unchecked by default => opens in same window
          attributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        },
      },
    },
    list: {
      properties: {
        styles: {
          useAttribute: false,
        },
        startIndex: true,
        reversed: true,
      },
    },
    table: {
      contentToolbar: [
        'tableColumn',
        'tableRow',
        'mergeTableCells',
        'tableProperties',
        'tableCellProperties',
      ],
      tableProperties: {
        borderColors: TEXT_COLOR_PALETTE,
        backgroundColors: TEXT_COLOR_PALETTE,
      },
      tableCellProperties: {
        borderColors: TEXT_COLOR_PALETTE,
        backgroundColors: TEXT_COLOR_PALETTE,
      },
    },
  }

  if (options.imageUpload) {
    toolbar.splice(toolbar.indexOf('link'), 0, 'insertImage')
    config.plugins?.push(
      Image,
      ImageCaption,
      ImageInsert,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      ProcoreImageUploadPlugin
    )
    config.image = {
      insert: {
        integrations: ['upload'],
      },
      upload: {
        types: ['avif', 'gif', 'jpeg', 'png', 'webp'],
      },
      resizeUnit: '%',
      toolbar: [
        'imageStyle:inline',
        'imageStyle:wrapText',
        'imageStyle:breakText',
        '|',
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'resizeImage:25',
        'resizeImage:50',
        'resizeImage:75',
        'resizeImage:original',
      ],
    }
    config.procoreImageUpload = options.imageUpload
  }

  return config
}

// Add stable data attributes to toolbar buttons for the styling (or testing) purposes
export const addButtonDataAttributes = (editor: ClassicEditor) => {
  const applyAttributes = () => {
    const toolbarElement = editor.ui.view.toolbar.element
    if (!toolbarElement) {
      return
    }

    // Get buttons in the main toolbar
    const toolbarChildren = Array.from(
      toolbarElement.querySelectorAll('.ck-toolbar__items > *')
    )

    // Get buttons hidden in "more" dropdown
    const moreDropdownButtons = Array.from(
      toolbarElement.querySelectorAll('.ck-dropdown__panel .ck-list .ck-button')
    )

    const allToolbarElements = [...toolbarChildren, ...moreDropdownButtons]

    // Create mapping based on toolbar configuration order
    const toolbarItems = (editor.config.get('toolbar') || []) as string[]
    let elementIndex = 0

    toolbarItems.forEach((itemName) => {
      const element = allToolbarElements[elementIndex]
      if (
        itemName !== '|' &&
        element &&
        !element.classList.contains('ck-toolbar__separator')
      ) {
        // Use the toolbar item name as the stable identifier
        element.setAttribute('data-cke-command', itemName)
      }
      elementIndex++
    })
  }

  // Apply attributes initially
  applyAttributes()

  // Re-apply when dropdown menus are opened (buttons get created dynamically)
  const toolbarElement = editor.ui.view.toolbar.element
  if (toolbarElement) {
    const observer = new MutationObserver(() => {
      applyAttributes()
    })
    observer.observe(toolbarElement, {
      childList: true,
      subtree: true,
    })
    editor.on('destroy', () => {
      observer.disconnect()
    })
  }
}
